// Australian super contributions: employer contributions per payment (capped
// at the maximum contribution base), salary sacrifice, and per-FY cap usage —
// concessional (with carry-forward of unused cap) and non-concessional (with
// the bring-forward arrangement). Pure; all money is integer CENTS.
//
// Simplifications (listed as future features in the finance plan):
//  - Contributions count in the FY of the payday they relate to (we assume the
//    fund receives them in time — the user doesn't track receipt dates).
//  - The bring-forward age limit (under 75 from 2022–23) isn't checked, and
//    the 2015–17 transitional bring-forward reassessment isn't modelled.
//  - Unreleased excess concessional contributions aren't counted towards the
//    non-concessional cap.

import { fyOf, fyQuarter } from '../fy.js';
import { roundMinor } from '../money.js';
import {
  CARRY_FORWARD,
  concessionalCap,
  maxContributionBase,
  nonConcessionalCap,
  sgRate,
  transferBalanceCap,
} from './super-tables.js';

/**
 * The salary-sacrifice amount for one pay: a percentage of the pre-sacrifice
 * gross, or a fixed amount per pay — never more than the gross.
 * @param {number} grossCents
 * @param {{ kind: 'percent'|'amount', value: number }|null} sacrifice — `value`
 *   is a decimal rate (0.1 = 10%) for 'percent', cents per pay for 'amount'.
 */
export function salarySacrificeFor(grossCents, sacrifice) {
  if (!sacrifice || !(sacrifice.value > 0)) return 0;
  const amount =
    sacrifice.kind === 'percent' ? roundMinor(grossCents * sacrifice.value) : sacrifice.value;
  return Math.min(Math.max(0, grossCents), Math.round(amount));
}

/** The MCB period a payment date falls in: the FY quarter, or the FY from 2026–27. */
function mcbPeriodKey(date) {
  const fy = fyOf(date);
  const mcb = maxContributionBase(fy);
  return { key: mcb.period === 'year' ? `${fy}` : `${fy}-Q${fyQuarter(date)}`, mcb, fy };
}

/**
 * Employer contributions for a run of payments FROM ONE EMPLOYER, in date
 * order. The contribution base is the ordinary-time earnings PLUS any amount
 * salary-sacrificed (employers must calculate SG on the pre-sacrifice amount).
 * When `capAtMcb` (the default), earnings beyond the maximum contribution base
 * for the quarter (to 2025–26) or FY (from 2026–27) attract no contribution.
 *
 * @param {Array<{ date: string, oteCents: number, sacrificeCents?: number, rate?: number }>} payments
 *   `rate` = the employer's super rate (decimal); defaults to the SG rate for the FY.
 * @returns {Array<{ date, fy, baseCents, cappedBaseCents, employerCents, rate, mcbLimited, mcbFallback }>}
 */
export function employerContributions(payments, { capAtMcb = true } = {}) {
  const used = new Map();
  return payments.map((p) => {
    const { key, mcb, fy } = mcbPeriodKey(p.date);
    const rate = p.rate ?? sgRate(fy);
    const baseCents = Math.max(0, (p.oteCents ?? 0) + (p.sacrificeCents ?? 0));
    const already = used.get(key) ?? 0;
    used.set(key, already + baseCents);
    const remaining = Math.max(0, mcb.amount * 100 - already);
    const cappedBaseCents = capAtMcb ? Math.min(baseCents, remaining) : baseCents;
    return {
      date: p.date,
      fy,
      baseCents,
      cappedBaseCents,
      employerCents: roundMinor(cappedBaseCents * rate),
      rate,
      mcbLimited: cappedBaseCents < baseCents,
      mcbFallback: mcb.fallback,
    };
  });
}

function bringForwardMultiple(fy, tsbCents, capCents) {
  const tbc = transferBalanceCap(fy);
  if (tbc === null) return 3; // before 1 July 2017: always 3 years
  if (tsbCents === null) return 3;
  if (tsbCents < tbc * 100 - 2 * capCents) return 3;
  if (tsbCents < tbc * 100 - capCents) return 2;
  return 1;
}

/**
 * Per-FY contribution cap usage.
 *
 * Concessional: the general cap, plus — when the total super balance (TSB) on
 * 30 June of the previous FY is under $500k — carried-forward unused cap from
 * up to the previous 5 FYs (accruing from 2018–19, so first usable 2019–20),
 * applied automatically, OLDEST first, only to the amount over the general
 * cap. Unused cap accrues whether or not you were a fund member, so a
 * missing FY between records counts as nil contributions. FYs BEFORE the
 * first record are unknown and accrue nothing (conservative).
 *
 * Non-concessional: nil when TSB ≥ the transfer balance cap; contributing
 * over the annual cap triggers a 3- or 2-year bring-forward (by TSB) whose
 * total cap is shared across the period.
 *
 * @param {Array<{ fy: number, concessionalCents?: number, nonConcessionalCents?: number,
 *   tsbPriorCents?: number|null, ageOnPriorJune30?: number }>} records
 * @returns {Array<object>} one entry per FY from the first record to the last.
 */
export function contributionCaps(records) {
  if (!records.length) return [];
  const byFy = new Map(records.map((r) => [r.fy, r]));
  const fys = [...byFy.keys()];
  const first = Math.min(...fys);
  const last = Math.max(...fys);
  const unused = new Map(); // fy → remaining unused concessional cap (cents)
  let period = null; // active NCC bring-forward { startFy, endFy, totalCents, usedCents }
  const out = [];

  for (let fy = first; fy <= last; fy++) {
    const r = byFy.get(fy) ?? {};
    const tsbPrior = r.tsbPriorCents ?? null;
    const cc = Math.max(0, r.concessionalCents ?? 0);
    const ncc = Math.max(0, r.nonConcessionalCents ?? 0);

    // --- Concessional + carry-forward ---
    const generalCents = concessionalCap(fy, r.ageOnPriorJune30) * 100;
    const window = [...unused.keys()].filter((y) => y >= fy - CARRY_FORWARD.years && y < fy);
    window.sort((a, b) => a - b);
    const eligible =
      fy > CARRY_FORWARD.firstUnusedFy &&
      (tsbPrior === null || tsbPrior < CARRY_FORWARD.tsbLimit * 100);
    const accrued = window.reduce((sum, y) => sum + unused.get(y), 0);
    const available = eligible ? accrued : 0;
    const over = Math.max(0, cc - generalCents);
    const carryUsed = Math.min(over, available);
    for (let i = 0, left = carryUsed; i < window.length && left > 0; i++) {
      const take = Math.min(left, unused.get(window[i]));
      unused.set(window[i], unused.get(window[i]) - take);
      left -= take;
    }
    for (const y of [...unused.keys()]) if (y < fy - CARRY_FORWARD.years + 1) unused.delete(y);
    const unusedAccrued = fy >= CARRY_FORWARD.firstUnusedFy ? Math.max(0, generalCents - cc) : 0;
    if (unusedAccrued > 0) unused.set(fy, unusedAccrued);
    const expiringCents = unused.get(fy - CARRY_FORWARD.years + 1) ?? 0;

    // --- Non-concessional + bring-forward ---
    const annualCents = nonConcessionalCap(fy) * 100;
    const tbc = transferBalanceCap(fy);
    const nil = tbc !== null && tsbPrior !== null && tsbPrior >= tbc * 100;
    if (period && fy > period.endFy) period = null;
    let nccAvailable;
    if (period) {
      nccAvailable = nil ? 0 : Math.max(0, period.totalCents - period.usedCents);
    } else {
      nccAvailable = nil ? 0 : annualCents;
      const multiple = bringForwardMultiple(fy, tsbPrior, annualCents);
      if (!nil && ncc > annualCents && multiple > 1) {
        period = {
          startFy: fy,
          endFy: fy + multiple - 1,
          totalCents: multiple * annualCents,
          usedCents: 0,
        };
        nccAvailable = period.totalCents;
      }
    }
    if (period) period.usedCents += Math.min(ncc, nccAvailable);

    out.push({
      fy,
      tsbPriorCents: tsbPrior,
      concessional: {
        contributedCents: cc,
        capCents: generalCents,
        carryForwardEligible: eligible,
        tsbAssumed: tsbPrior === null,
        carryForwardAccruedCents: accrued,
        carryForwardAvailableCents: available,
        carryForwardUsedCents: carryUsed,
        excessCents: over - carryUsed,
        unusedAccruedCents: unusedAccrued,
        // Unused cap usable next FY (if eligible), and the part that expires if unused then.
        carryForwardRemainingCents: [...unused.values()].reduce((a, b) => a + b, 0),
        expiringCents,
      },
      nonConcessional: {
        contributedCents: ncc,
        annualCapCents: nil ? 0 : annualCents,
        availableCents: nccAvailable,
        bringForward: period
          ? { startFy: period.startFy, endFy: period.endFy, totalCents: period.totalCents }
          : null,
        excessCents: Math.max(0, ncc - nccAvailable),
      },
    });
  }
  return out;
}
